import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  GeoJSONSourceSpecification,
  LineLayerSpecification,
} from 'maplibre-gl';

import { GEOSERVER_URL, ITASCA_URL } from '@/constants/urls';
import { transformCDLStats } from '@/features/insights/cropHistory/imageDisplay';
import { ICDLStatsResponse, ICDLVectorResponse, ILayer } from '@/models/cdl';
import { AcreCompsResponse, ICompCollection } from '@/models/comps';
import {
  IElevationColorResponse,
  IElevationContourResponse,
} from '@/models/elevation';
import { AcresFeature } from '@/models/features';
import { NDVIResponse } from '@/models/ndvi';
import { ISatelliteHistoryV2Response } from '@/models/satelliteHistory';

type Boundary = AcresFeature | null;

export type GetHistoricalImageSource = 'gcp' | 'aws';

export interface GetGeoServerQuery {
  boundary: Boundary;
  /** This needs to be a string */
  year: number | string;
  source?: GetHistoricalImageSource;
}
export interface GetElevationQuery {
  boundary: Boundary;
  units: 'feet';
  line_sep?: number;
}

export interface CDLImageResponseQuery {
  cdlMapLayer: ICDLVectorResponse;
  cdlImageData: ILayer;
}

type IGeoJsonLayer = LineLayerSpecification & {
  source: GeoJSONSourceSpecification;
};

export interface IElevationGeojsonResponse {
  layers: IGeoJsonLayer[];
}

export interface ICompResponseQuery {
  authToken: string;
}

export const geoserverApi = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: `${GEOSERVER_URL}/geoserver`,
    prepareHeaders: (headers) => headers,
  }),
  endpoints: (builder) => ({
    getCDLImage: builder.query<CDLImageResponseQuery, GetGeoServerQuery>({
      query: (data) => ({
        body: data,
        credentials: 'include',
        method: 'POST',
        url: 'cdl_image',
      }),
      // eslint-disable-next-line no-empty-pattern, @typescript-eslint/no-unused-vars
      transformResponse: (response: ILayer, _, {}) => {
        const CDLMapLayer = {
          layers: [response],
        };

        return {
          cdlImageData: response,
          cdlMapLayer: CDLMapLayer,
        };
      },
    }),
    getCDLStats: builder.query<ICDLStatsResponse, GetGeoServerQuery>({
      query: (data) => ({
        body: data,
        credentials: 'include',
        method: 'POST',
        url: 'cdl_stats',
      }),
      transformResponse: (response: ICDLStatsResponse) =>
        transformCDLStats(response),
    }),
    getElevationColor: builder.query<
      IElevationColorResponse,
      GetElevationQuery
    >({
      query: (data) => ({
        body: data,
        credentials: 'include',
        method: 'POST',
        url: 'elevation_color',
      }),
      transformResponse: (response: IElevationColorResponse) => response,
    }),
    getElevationContour: builder.query<
      IElevationContourResponse,
      GetElevationQuery
    >({
      query: (data) => ({
        body: data,
        credentials: 'include',
        method: 'POST',
        url: 'elevation_contour',
      }),
      transformResponse: (response: IElevationContourResponse) => response,
    }),
    getElevationGeoJson: builder.query<
      IElevationGeojsonResponse,
      GetElevationQuery
    >({
      query: (data) => ({
        body: data,
        credentials: 'include',
        method: 'POST',
        url: 'elevation_geojson',
      }),
      transformResponse: (response: IElevationGeojsonResponse) => response,
    }),
    getInternalComps: builder.query<ICompCollection, ICompResponseQuery>({
      query: () => ({
        credentials: 'include',
        url: `${ITASCA_URL}/comps/`,
      }),
      transformResponse: (response: AcreCompsResponse[]): ICompCollection => {
        const compCollection: ICompCollection = {
          acretrader: {
            features: [],
            type: 'FeatureCollection',
          },
          auction: {
            features: [],
            type: 'FeatureCollection',
          },
          listing: {
            features: [],
            type: 'FeatureCollection',
          },
          rejected: {
            features: [],
            type: 'FeatureCollection',
          },
          sale: {
            features: [],
            type: 'FeatureCollection',
          },
        };

        response.forEach((item) => {
          if (item.comp_type) {
            const newFeature = {
              geometry: item.boundary_centroid,
              id: item.id,
              properties: {
                ...item,
              },
              type: 'Feature',
            };

            compCollection[item.comp_type].features.push(newFeature);
          }
        });

        return compCollection;
      },
    }),
    getNDVI: builder.query<NDVIResponse, GetGeoServerQuery>({
      query: (data) => ({
        body: {
          boundary: data.boundary,
          end: `${data.year}-12-31`,
          start: `${data.year}-01-01`,
        },
        credentials: 'include',
        method: 'POST',
        url: 'ndvi',
      }),
      transformResponse: (response: NDVIResponse) => response,
    }),
    getSatelliteHistory: builder.query<
      ISatelliteHistoryV2Response,
      GetGeoServerQuery
    >({
      query: (data) => {
        let url = 'historical_image';

        if (data.source) {
          url = `${url}/${data.source}`;
        }

        return {
          body: {
            boundary: data.boundary,
            year: data.year,
          },
          credentials: 'include',
          method: 'POST',
          url,
        };
      },
      transformResponse: (response: ISatelliteHistoryV2Response) => response,
    }),
  }),
  reducerPath: 'geoserverApi',
});

export const {
  useGetNDVIQuery,
  useGetElevationColorQuery,
  useGetElevationContourQuery,
  useGetCDLStatsQuery,
  useGetCDLImageQuery,
  useGetSatelliteHistoryQuery,
  useGetElevationGeoJsonQuery,
  useGetInternalCompsQuery,
} = geoserverApi;
